import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import {
  PageHeader, AdminCard, DataTable, Btn, Modal, Field, Input, Textarea, PageLoader,
} from "@/admin/components";
import { formatINR } from "@/lib/format";

const EMPTY_FORM = {
  name: "", description: "", image_url: "", wholesale_price_inr: 0,
  selling_price_inr: 0, astrologer_commission_inr: 0, supplier_sku: "", active: true,
};

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-[#111]" : "bg-[#ddd]"}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
  </button>
);

export default function Products() {
  const [products, setProducts] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/products");
    setProducts(data.products);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditTarget(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({
      name: p.name, description: p.description, image_url: p.image_url || "",
      wholesale_price_inr: p.wholesale_price_inr, selling_price_inr: p.selling_price_inr,
      astrologer_commission_inr: p.astrologer_commission_inr, supplier_sku: p.supplier_sku || "",
      active: p.active,
    });
    setModalOpen(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setNum = (key) => (e) => setForm((f) => ({ ...f, [key]: parseInt(e.target.value, 10) || 0 }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      if (editTarget) {
        await adminApi.put(`/admin/products/${editTarget.id}`, form);
        toast.success("Product updated");
      } else {
        await adminApi.post("/admin/products", form);
        toast.success("Product created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      await adminApi.put(`/admin/products/${p.id}`, { ...p, active: !p.active });
      toast.success(p.active ? "Product deactivated" : "Product activated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const columns = [
    {
      key: "image_url", label: "Image",
      render: (r) => r.image_url
        ? <img src={r.image_url} alt={r.name} className="h-12 w-12 rounded-[8px] object-cover border border-[#eee]" />
        : <div className="h-12 w-12 rounded-[8px] bg-[#f2f0ec]" />,
    },
    {
      key: "name", label: "Product",
      render: (r) => (
        <div>
          <div className="font-semibold text-[#111]">{r.name}</div>
          <div className="text-[11px] text-[#888] mt-0.5 line-clamp-1">{r.description}</div>
        </div>
      ),
    },
    { key: "selling_price_inr", label: "Price", render: (r) => formatINR(r.selling_price_inr) },
    { key: "wholesale_price_inr", label: "Cost", render: (r) => formatINR(r.wholesale_price_inr) },
    { key: "astrologer_commission_inr", label: "Commission", render: (r) => formatINR(r.astrologer_commission_inr) },
    { key: "order_count", label: "Orders", render: (r) => <span className="font-medium">{r.order_count ?? 0}</span> },
    {
      key: "active", label: "Active",
      render: (r) => (
        <Toggle
          checked={r.active}
          onChange={() => toggleActive(r)}
          testid={`toggle-active-${r.id}`}
        />
      ),
    },
    {
      key: "actions", label: "",
      render: (r) => (
        <Btn onClick={() => openEdit(r)} testid={`edit-product-${r.id}`}>Edit</Btn>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Products"
        subtitle="Manage the product catalogue"
        right={<Btn onClick={openAdd} testid="add-product-btn">+ Add Product</Btn>}
      />

      {products === null ? <PageLoader /> : (
        <AdminCard>
          <DataTable columns={columns} rows={products} empty="No products yet" />
        </AdminCard>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? "Edit Product" : "Add Product"}>
        <div className="space-y-4">
          <Field label="Name">
            <Input value={form.name} onChange={set("name")} placeholder="Product name" data-testid="product-name-input" />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={set("description")} data-testid="product-desc-input" rows={3} />
          </Field>
          <Field label="Image URL">
            <Input value={form.image_url} onChange={set("image_url")} placeholder="https://..." data-testid="product-image-input" />
          </Field>
          <Field label="Supplier SKU">
            <Input value={form.supplier_sku} onChange={set("supplier_sku")} placeholder="supplier-sku" data-testid="product-sku-input" />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Selling Price (₹)">
              <Input type="number" value={form.selling_price_inr} onChange={setNum("selling_price_inr")} data-testid="product-price-input" />
            </Field>
            <Field label="Wholesale (₹)">
              <Input type="number" value={form.wholesale_price_inr} onChange={setNum("wholesale_price_inr")} data-testid="product-wholesale-input" />
            </Field>
            <Field label="Commission (₹)">
              <Input type="number" value={form.astrologer_commission_inr} onChange={setNum("astrologer_commission_inr")} data-testid="product-commission-input" />
            </Field>
          </div>
          <div className="flex items-center gap-3">
            <Toggle checked={form.active} onChange={(v) => setForm((f) => ({ ...f, active: v }))} testid="product-active-toggle" />
            <span className="text-[13px] text-[#555]">Active (visible on profiles)</span>
          </div>
          <div className="flex gap-3 pt-2">
            <Btn variant="secondary" onClick={() => setModalOpen(false)} testid="product-cancel-btn">Cancel</Btn>
            <Btn onClick={save} disabled={busy} testid="product-save-btn">{busy ? "Saving…" : "Save"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
